/* eslint-disable no-console */
function parseGID(gid) {
  return gid.split('/').splice(-1)[0];
}

/** Debounce delay for cart updates to prevent HTTP 409 conflicts */
const CART_UPDATE_DEBOUNCE_MS = 300;

/** Set on gift card lines sent to a recipient (see product-form.js) */
const GIFT_RECIPIENT_PROPERTY = '__shopify_send_gift_card_to_recipient';

function hasGiftRecipient(properties) {
  return Boolean(properties && properties[GIFT_RECIPIENT_PROPERTY]);
}

class AjaxService {
  static name = 'ajax';

  constructor() {
    // Pending updates queue for debouncing
    this._pendingUpdates = {};
    this._updateTimeout = null;
    this._updateResolvers = [];
  }

  /**
   * Gets a cart instance from a cart ID
   * @param {string} id cart id
   * @returns formatted cart object
   */
  async get() {
    return fetch(`${window.Shopify.routes.root}cart.js`).then((response) =>
      response.json(),
    );
  }

  async add(_, payload) {
    // add _cart_order property to find last added item
    let cartOrder = 1;
    const cartData = await this.get();
    const { items } = cartData;
    if (cartData.item_count > 0) {
      const maxCartOrder = Math.max(
        ...items.map((item) => item.properties._cart_order),
      );
      cartOrder = maxCartOrder + 1;
    }

    const payloadArray = Array.isArray(payload) ? payload : [payload];

    // Shopify splits a variant into a new line whenever properties differ
    // from an existing line (e.g. _cart_label/_FR_Color refreshed after a
    // tag or policy change), even though it's the same variant ID - this
    // causes duplicate Tlog lines for the same item. Merge into the
    // existing line instead, keeping the newly submitted properties.
    // Gift cards sent to a recipient are excluded: each line can carry its
    // own recipient info and must stay split. Physical/self-purchase gift
    // cards have no recipient properties, so they can merge like anything
    // else.
    const preparedItems = payloadArray.map(
      ({ merchandiseId, quantity, attributes, id }) => {
        const properties = (attributes || []).reduce(
          (acc, cur) => ({ ...acc, [cur.key]: cur.value }),
          {},
        );
        const variantId = parseGID(merchandiseId) || parseGID(id);
        const existingItem = hasGiftRecipient(properties)
          ? undefined
          : items.find(
            (item) => item.id === +variantId
              && !hasGiftRecipient(item.properties),
          );
        properties._cart_order = existingItem
          ? existingItem.properties._cart_order
          : cartOrder;
        return {
          variantId, quantity, properties, existingItem,
        };
      },
    );

    const mergesByKey = new Map();
    preparedItems
      .filter((item) => item.existingItem)
      .forEach(({ existingItem, quantity, properties }) => {
        const merge = mergesByKey.get(existingItem.key) || {
          existingItem,
          addedQuantity: 0,
          properties: {},
        };
        merge.addedQuantity += quantity;
        merge.properties = { ...merge.properties, ...properties };
        mergesByKey.set(existingItem.key, merge);
      });

    const toAdd = preparedItems.filter((item) => !item.existingItem);

    // Sequential to avoid Shopify cart race conditions with parallel mutations
    await [...mergesByKey.values()].reduce(
      (seq, { existingItem, addedQuantity, properties }) => seq.then(
        () => fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          body: JSON.stringify({
            id: existingItem.key,
            quantity: existingItem.quantity + addedQuantity,
            properties: { ...existingItem.properties, ...properties },
          }),
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }),
      ),
      Promise.resolve(),
    );

    if (toAdd.length > 0) {
      const body = {
        items: toAdd.map(({ variantId, quantity, properties }) => ({
          id: variantId,
          quantity,
          properties,
        })),
      };

      await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });
    }

    return this.get();
  }

  async remove(_, payload) {
    if (!payload) return null;
    const body = {
      updates: Object.fromEntries(payload.map((id) => [id, 0])),
    };
    return fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }).then((response) => response.json());
  }

  /**
   * Internal method to execute the actual cart update
   * @private
   */
  async _executeCartUpdate() {
    const updates = { ...this._pendingUpdates };
    const resolvers = [...this._updateResolvers];

    // Clear pending state
    this._pendingUpdates = {};
    this._updateResolvers = [];
    this._updateTimeout = null;

    if (Object.keys(updates).length === 0) {
      // Nothing to update, resolve with current cart
      const cart = await this.get();
      resolvers.forEach(({ resolve }) => resolve(cart));
      return;
    }

    try {
      const url = `${window.Shopify.routes.root}cart/update.js`;
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({ updates }),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      const result = await response.json();
      resolvers.forEach(({ resolve }) => resolve(result));
    } catch (error) {
      console.error('[AjaxService] Cart update failed:', error);
      resolvers.forEach(({ reject }) => reject(error));
    }
  }

  /**
   * Updates cart items with debouncing to prevent HTTP 409 conflicts.
   * Multiple rapid calls are batched into a single request.
   */
  async update(_, payload) {
    if (!payload) return null;

    // For note or attributes updates, execute immediately (no debounce)
    if (payload.note || payload.attributes) {
      const body = payload.note
        ? { note: payload.note }
        : { attributes: payload.attributes };

      return fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }).then((response) => response.json());
    }

    // For quantity updates, use debouncing
    return new Promise((resolve, reject) => {
      // Merge updates into pending queue
      payload.forEach((item) => {
        this._pendingUpdates[item.id] = item.quantity;
      });

      // Add resolver to queue
      this._updateResolvers.push({ resolve, reject });

      // Clear existing timeout
      if (this._updateTimeout) {
        clearTimeout(this._updateTimeout);
      }

      // Schedule debounced update
      this._updateTimeout = setTimeout(() => {
        this._executeCartUpdate();
      }, CART_UPDATE_DEBOUNCE_MS);
    });
  }

  // AJAX Cart only supports updating properties on one line item at a time so payload only accepts an object
  async updateAttributes(_, payload) {
    if (!payload) return null;
    const { attributes, id, quantity } = payload;
    const properties = (attributes || []).reduce(
      (acc, cur) => ({ ...acc, [cur.key]: cur.value }),
      {},
    );
    const body = {
      id: parseGID(id),
    };
    if (quantity) body.quantity = quantity;
    if (attributes) body.properties = properties;
    return fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }).then((response) => response.json());
  }

  async updateCartAttributes(_, payload) {
    if (!payload) return null;
    const response = await fetch(
      `${window.Shopify.routes.root}cart/update.js`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      },
    );
    if (!response.ok) {
      console.error(
        `[AjaxService] updateCartAttributes failed: ${response.status}`,
      );
      return null;
    }
    return response.json();
  }
}

const AjaxServiceSingleton = new AjaxService();
export default AjaxServiceSingleton;
